import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    TextField,
    IconButton,
    Tooltip,
    MenuItem,
    Checkbox,
    Select,
    InputLabel,
    FormControl,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { AuthContext } from "../../context/AuthContext";
import StudentDetailsModal from "../../components/Modals/StudentDetailsModal";

const StudentsPage = () => {
    const { user } = useContext(AuthContext);

    // ── raw data
    const [students, setStudents] = useState([]);
    const [schools, setSchools] = useState([]);
    const [payments, setPayments] = useState([]);

    // ── filters
    const [firstNameFilter, setFirstNameFilter] = useState("");
    const [lastNameFilter, setLastNameFilter] = useState("");
    const [selectedSite, setSelectedSite] = useState("");
    const [selectedGrade, setSelectedGrade] = useState("");
    const [studentIdFilter, setStudentIdFilter] = useState("");
    const [paymentStatusFilter, setPaymentStatusFilter] = useState(""); // "" | "new" | "applied"

    // ── inline edit / detail modal
    const [editingId, setEditingId] = useState(null);
    const [inlineEditData, setInlineEditData] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [modalEditData, setModalEditData] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);

    // ── load schools once
    useEffect(() => {
        axios
            .get("https://figazuresql.swancreate.com/api/swa/SchoolMst")
            .then(({ data }) => setSchools(data))
            .catch((e) => console.error(e));
    }, []);

    // ── fetch students & payments on demand
    const fetchStudents = () =>
        axios
            .get("https://figazuresql.swancreate.com/api/swa/Students")
            .then(({ data }) => setStudents(data))
            .catch((e) => console.error(e));

    const fetchPayments = () =>
        axios
            .get("https://figazuresql.swancreate.com/api/swa/Payments")
            .then(({ data }) => setPayments(data))
            .catch((e) => console.error(e));

    // ── handle Search
    const handleSearch = async (e) => {
        e.preventDefault();
        await fetchStudents();
        await fetchPayments();
    };

    // ── handle Enter key in filters
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSearch(e);
        }
    };

    // ── unique dropdown options
    const uniqueSites = Array.from(new Set(schools.map((s) => s.SchoolName).filter(Boolean)));
    const uniqueGrades = Array.from(new Set(students.map((s) => s.Grade).filter(Boolean)));

    // ── client-side filter of student list
    const filteredStudents = students.filter((s) => {
        const baseFilters = [
            !firstNameFilter || s.FirstName?.toLowerCase().includes(firstNameFilter.toLowerCase()),
            !lastNameFilter || s.LastName?.toLowerCase().includes(lastNameFilter.toLowerCase()),
            !selectedSite || s.SchoolName === selectedSite,
            !selectedGrade || s.Grade === selectedGrade,
            !studentIdFilter || s.StudentIDNumber?.toLowerCase().includes(studentIdFilter.toLowerCase()),
        ].every(Boolean);
        if (!baseFilters) return false;

        // Payment status filter
        if (paymentStatusFilter === "new") {
            return payments.some((p) => p.StudentGuidID === s.StudentguidID && !p.isUpdated);
        } else if (paymentStatusFilter === "applied") {
            return payments.some((p) => p.StudentGuidID === s.StudentguidID && p.isUpdated);
        }
        return true;
    });

    // ── flatten payments for CSV and for Apply All
    const filteredPayments = payments.filter((p) => {
        // student in filteredStudents?
        const studentMatch = filteredStudents.some((s) => s.StudentguidID === p.StudentGuidID);
        if (!studentMatch) return false;
        if (paymentStatusFilter === "new") return !p.isUpdated;
        if (paymentStatusFilter === "applied") return !!p.isUpdated;
        return true;
    });

    // ── CSV download
    const downloadCSV = () => {
        const header = [
            "StudentNumber",
            "StudentFName",
            "StudentLName",
            "SchoolName",
            "PaymentAmount",
            "DistrictReceivableAmount",
            "PaymentReference",
        ].join(",") + "\n";

        const rows = filteredPayments.map((p) =>
            [
                p.StudentNumber,
                p.StudentFName,
                p.StudentLName,
                p.SchoolName,
                p.PaymentAmount,
                p.DistrictReceivableAmount,
                p.PaymentReference,
            ]
                .map((v) => `"${v ?? ""}"`)
                .join(",")
        ).join("\n");

        const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "payments_export.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    // ── Apply all new payments
    const applyAllPayments = async () => {
        const toApply = filteredPayments.filter((p) => !p.isUpdated);
        await Promise.all(
            toApply.map((p) =>
                axios.put(
                    `https://figazuresql.swancreate.com/api/swa/Payments/${p.apiid}`,
                    { isUpdated: true, UpdatedDate: new Date().toISOString() }
                )
            )
        );
        await fetchPayments();
    };

    // ── NEW helpers
    const copyStudentId = (id) => navigator.clipboard.writeText(id).catch(console.error);

    const toggleRefund = async (payment, checked) => {
        try {
            await axios.put(
                `https://figazuresql.swancreate.com/api/swa/Payments/${payment.apiid}`,
                { isRefunded: checked, isUpdated: checked ? 1 : payment.isUpdated }
            );
            await fetchPayments();
        } catch (err) {
            console.error(err);
        }
    };

    // ── inline edit handlers
    const handleInlineEditClick = (student, e) => {
        e.stopPropagation();
        setEditingId(student.apiid);
        setInlineEditData({
            StudentIDNumber: student.StudentIDNumber,
            FirstName: student.FirstName,
            LastName: student.LastName,
            SchoolName: student.SchoolName,
            Grade: student.Grade,
            ImageURL: student.ImageURL || "",
        });
    };
    const handleInlineInputChange = (e) => setInlineEditData((p) => ({ ...p, [e.target.name]: e.target.value }));
    const handleInlineSaveClick = async (apiid, e) => {
        e.stopPropagation();
        const { apiid: omit, ...payload } = inlineEditData;
        await axios.put(`https://figazuresql.swancreate.com/api/swa/Students/${apiid}`, payload);
        setStudents((prev) => prev.map((s) => (s.apiid === apiid ? { ...s, ...payload } : s)));
        setEditingId(null);
    };
    const handleInlineCancelClick = (e) => { e.stopPropagation(); setEditingId(null); };

    // ── detail modal handlers
    const handleRowClick = (student) => {
        if (editingId) return;
        setSelectedStudent(student);
        setModalEditData(student);
        setModalOpen(true);
    };
    const handleModalInputChange = (e) => setModalEditData((p) => ({ ...p, [e.target.name]: e.target.value }));
    const handleModalSave = async () => {
        if (!selectedStudent) return;
        const { apiid: omit, ...payload } = modalEditData;
        await axios.put(`https://figazuresql.swancreate.com/api/swa/Students/${selectedStudent.apiid}`, payload);
        setStudents((prev) => prev.map((s) => (s.apiid === selectedStudent.apiid ? { ...s, ...payload } : s)));
        setModalOpen(false);
    };
    const handleModalClose = () => { setModalOpen(false); setSelectedStudent(null); };

    // ── image upload handlers
    const handleFileChange = (e) => setSelectedFile(e.target.files[0] || null);
    const handleUploadImage = async () => {
        if (!selectedFile || !selectedStudent) return;
        const ext = selectedFile.name.slice(selectedFile.name.lastIndexOf("."));
        const fileName = `${selectedStudent.apiid}${ext}`;
        const fd = new FormData();
        fd.append("file", new File([selectedFile], fileName));
        fd.append("folder", "timeandattendance");
        await axios.post("https://media.swancreate.com/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        const newUrl = `https://media.swancreate.com/images/timeandattendance/${fileName}`;
        const { apiid: omit, ...payload } = { ...modalEditData, ImageURL: newUrl };
        await axios.put(`https://figazuresql.swancreate.com/api/swa/Students/${selectedStudent.apiid}`, payload);
        setModalEditData((p) => ({ ...p, ImageURL: newUrl }));
        setStudents((prev) => prev.map((s) => (s.apiid === selectedStudent.apiid ? { ...s, ImageURL: newUrl } : s)));
    };

    // ── apply a single payment to that student
    const applyPayment = async (payment, student) => {
        try {
            const oldBal = Number(student.Account_Balance) || 0;
            const amt = Number(payment.PaymentAmount) || 0;
            const newBal = oldBal + amt;
            await axios.put(`https://figazuresql.swancreate.com/api/swa/Students/${student.apiid}`, { Account_Balance: newBal });
            await axios.put(`https://figazuresql.swancreate.com/api/swa/Payments/${payment.apiid}`, { isUpdated: true, UpdatedDate: new Date().toISOString() });
            await fetchStudents();
            await fetchPayments();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Box m={2} p={3} sx={{ bgcolor: "#121212", color: "#EFFFFD", borderRadius: 2 }}>
            <Typography variant="h4" mb={3} textAlign="center">
                Students Dashboard
            </Typography>

            {/* ── filter form ───────────────────────────── */}
            <Box
                component="form"
                onSubmit={handleSearch}
                display="flex"
                flexWrap="wrap"
                gap={2}
                mb={3}
            >
                <TextField
                    label="First Name"
                    value={firstNameFilter}
                    onChange={(e) => setFirstNameFilter(e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={{ minWidth: 160 }}
                />
                <TextField
                    label="Last Name"
                    value={lastNameFilter}
                    onChange={(e) => setLastNameFilter(e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={{ minWidth: 160 }}
                />
                <TextField
                    label="Site"
                    select
                    value={selectedSite}
                    onChange={(e) => setSelectedSite(e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={{ minWidth: 200 }}
                >
                    <MenuItem value="">All</MenuItem>
                    {uniqueSites.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Grade"
                    select
                    value={selectedGrade}
                    onChange={(e) => setSelectedGrade(e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={{ minWidth: 200 }}
                >
                    <MenuItem value="">All</MenuItem>
                    {uniqueGrades.map((g) => (
                        <MenuItem key={g} value={g}>{g}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Student ID"
                    value={studentIdFilter}
                    onChange={(e) => setStudentIdFilter(e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={{ minWidth: 200 }}
                />

                {/* Payment Status dropdown */}
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel sx={{ color: "#EFFFFD" }}>Payment Status</InputLabel>
                    <Select
                        value={paymentStatusFilter}
                        onChange={(e) => setPaymentStatusFilter(e.target.value)}
                        sx={{
                            color: "#EFFFFD",
                            ".MuiOutlinedInput-notchedOutline": { borderColor: "#84BD00" },
                        }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="new">New Payments</MenuItem>
                        <MenuItem value="applied">Applied Payments</MenuItem>
                    </Select>
                </FormControl>

                {/* CSV Download & Apply All */}
                <Button
                    variant="contained"
                    sx={{ bgcolor: "#84BD00", color: "#121212" }}
                    onClick={downloadCSV}
                >
                    Download CSV
                </Button>
                <Button
                    variant="contained"
                    sx={{ bgcolor: "#84BD00", color: "#121212" }}
                    onClick={applyAllPayments}
                >
                    Apply All Payments
                </Button>

                <Button
                    type="submit"
                    variant="contained"
                    sx={{ bgcolor: "#84BD00", color: "#121212", ml: "auto" }}
                >
                    SEARCH
                </Button>
            </Box>

            {/* ── students table with payments & balance ────────────────── */}
            <TableContainer component={Paper} sx={{ bgcolor: "#1A1A2E", borderRadius: 1 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ color: "#EFFFFD" }}>Image</TableCell>
                            <TableCell sx={{ color: "#EFFFFD" }}>Student&nbsp;ID</TableCell>
                            <TableCell sx={{ color: "#EFFFFD" }}>First&nbsp;Name</TableCell>
                            <TableCell sx={{ color: "#EFFFFD" }}>Last&nbsp;Name</TableCell>
                            <TableCell sx={{ color: "#EFFFFD" }}>School</TableCell>
                            <TableCell sx={{ color: "#EFFFFD" }}>Grade</TableCell>
                            <TableCell sx={{ color: "#EFFFFD" }}>Balance</TableCell>
                            <TableCell sx={{ color: "#EFFFFD" }}>Payment&nbsp;Amt</TableCell>
                            <TableCell sx={{ color: "#EFFFFD" }}>Updated?</TableCell>
                            <TableCell sx={{ color: "#EFFFFD" }}>Refunded?</TableCell>
                            <TableCell sx={{ color: "#EFFFFD" }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredStudents.map((s, i) => {
                            const pend = payments.filter(
                                (p) => p.StudentGuidID === s.StudentguidID && !p.isUpdated
                            );

                            return (
                                <Tooltip key={s.apiid} title="Click for details" arrow>
                                    <TableRow
                                        onClick={() => handleRowClick(s)}
                                        sx={{
                                            bgcolor: i % 2 === 0 ? "#1A1A2E" : "#2A1A2E",
                                            cursor: "pointer",
                                            "&:hover": { bgcolor: "#343a40" },
                                        }}
                                    >
                                        <TableCell>
                                            {s.ImageURL ? (
                                                <img
                                                    src={s.ImageURL}
                                                    alt=""
                                                    style={{ width: 40, height: 40, borderRadius: 20 }}
                                                />
                                            ) : (
                                                "No Image"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {s.StudentIDNumber}
                                            <Tooltip title="Copy ID">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        copyStudentId(s.StudentIDNumber);
                                                    }}
                                                >
                                                    <ContentCopyIcon
                                                        sx={{ color: "#B0E0E6", fontSize: 16 }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{s.FirstName}</TableCell>
                                        <TableCell>{s.LastName}</TableCell>
                                        <TableCell>{s.SchoolName}</TableCell>
                                        <TableCell>{s.Grade || "—"}</TableCell>
                                        <TableCell sx={{ color: "#EFFFFD" }}>
                                            {Number(s.Account_Balance || 0).toLocaleString("en-US", {
                                                style: "currency",
                                                currency: "USD",
                                            })}
                                        </TableCell>
                                        <TableCell sx={{ color: "#EFFFFD" }}>
                                            {pend.length
                                                ? pend
                                                    .map((p) =>
                                                        Number(p.PaymentAmount || 0).toLocaleString("en-US", {
                                                            style: "currency",
                                                            currency: "USD",
                                                        })
                                                    )
                                                    .join(", ")
                                                : "—"}
                                        </TableCell>
                                        <TableCell>
                                            {pend.length ? (
                                                pend.map((p) => (
                                                    <Checkbox
                                                        key={p.apiid}
                                                        checked={!!p.isUpdated}
                                                        disabled
                                                        sx={{ color: "#EFFFFD" }}
                                                    />
                                                ))
                                            ) : (
                                                <Checkbox checked disabled sx={{ color: "#EFFFFD" }} />
                                            )}
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            {pend.length ? (
                                                pend.map((p) => (
                                                    <Checkbox
                                                        key={p.apiid}
                                                        checked={!!p.isRefunded}
                                                        onChange={(e) =>
                                                            toggleRefund(p, e.target.checked)
                                                        }
                                                        sx={{ color: "#EFFFFD" }}
                                                    />
                                                ))
                                            ) : (
                                                <Checkbox checked disabled sx={{ color: "#EFFFFD" }} />
                                            )}
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            {pend.map((p) => (
                                                <Button
                                                    key={p.apiid}
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => applyPayment(p, s)}
                                                    sx={{ bgcolor: "#84BD00", color: "#121212", m: 0.5 }}
                                                >
                                                    Apply
                                                </Button>
                                            ))}
                                            {editingId === s.apiid ? (
                                                <>
                                                    <IconButton
                                                        onClick={(e) => handleInlineSaveClick(s.apiid, e)}
                                                    >
                                                        <SaveIcon sx={{ color: "#B0E0E6" }} />
                                                    </IconButton>
                                                    <IconButton onClick={handleInlineCancelClick}>
                                                        <CancelIcon sx={{ color: "#B0E0E6" }} />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <IconButton
                                                    onClick={(e) => handleInlineEditClick(s, e)}
                                                >
                                                    <EditIcon sx={{ color: "#B0E0E6" }} />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </Tooltip>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* detail modal */}
            <StudentDetailsModal
                open={modalOpen}
                onClose={handleModalClose}
                student={selectedStudent}
                editData={modalEditData}
                onChange={handleModalInputChange}
                onSave={handleModalSave}
                onFileChange={handleFileChange}
                onUploadImage={handleUploadImage}
            />
        </Box>
    );
};

export default StudentsPage;
